import React, { useState, useContext } from "react";
import { X, Check } from "lucide-react";
import {
  plans,
  oneTimeStudy,
  studentPlan,
} from "../../assets/data/pricingData";
import Cookies from "js-cookie";
import { SessionContext } from "../../context/sessionContext";

const PricingModal = ({ isOpen, onClose, currentPlanName }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { session, setSession } = useContext(SessionContext);

  const allPlans = [...plans, oneTimeStudy, studentPlan];

  const handlePlanSelect = async (plan) => {
    setSelectedPlan(plan.name);
    setLoading(true);

    try {
      const token = Cookies.get("token");

      if (!token) {
        window.location.href = "/log-in";
        return;
      }
      localStorage.setItem("upgradeFlow", "true");
      localStorage.setItem(
        "paymentContext",
        JSON.stringify({
          source: "UPGRADE_PLAN",
          planName: plan.name,
        }),
      );

      localStorage.setItem(
        "selectedPlan",
        JSON.stringify({
          planName: plan.name,
          amount: plan.price || 0,
        }),
      );

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payment/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          credentials: "include",
          body: JSON.stringify({
            planName: plan.name,
            amount: plan.price || 0,
            userId: session?.user?.user_id || session?.user?.id,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok || !data.url) {
        throw new Error(data.message || "Failed to create payment session");
      }
      window.location.href = data.url;
    } catch (error) {
      console.error("Error creating payment session:", error);
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "2rem",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          backgroundColor: "#1a1a1a",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "1200px",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <div
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "#1a1a1a",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "1.5rem 2rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 10,
          }}
        >
          <div>
            <h2 style={{ color: "white", fontSize: "1.5rem", margin: 0 }}>
              Upgrade Your Plan
            </h2>
            <p
              style={{
                color: "#9ca3af",
                fontSize: "0.9rem",
                margin: "0.25rem 0 0 0",
              }}
            >
              Current Plan:{" "}
              <span style={{ color: "#3b82f6", fontWeight: 600 }}>
                {currentPlanName}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              background: "none",
              border: "none",
              color: "#9ca3af",
              cursor: loading ? "not-allowed" : "pointer",
              padding: " 0.5rem",
            }}
          >
            <X size={24} />
          </button>
        </div>
        <div
          style={{
            padding: "2rem",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "1.5rem",
            justifyContent: "center",
          }}
        >
          {allPlans.map((plan, index) => {
            const isCurrentPlan = plan.name === currentPlanName;
            const isLoading = loading && selectedPlan === plan.name;

            return (
              <div
                key={index}
                style={{
                  backgroundColor: isCurrentPlan
                    ? "rgba(255, 255, 255, 0.03)"
                    : "rgba(255, 255, 255, 0.03)",
                  border: isCurrentPlan
                    ? "2px solid #3b82f6"
                    : "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "12px",
                  padding: "1.5rem",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  transition: "all 0.3s ease",
                }}
              >
                {isCurrentPlan && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-10px",
                      right: "20px",
                      backgroundColor: "#3b82f6",
                      color: "white",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "999px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    CURRENT PLAN
                  </div>
                )}

                {plan.highlighted && !isCurrentPlan && (
                  <div
                    style={{
                      position: "absolute",
                      top: "-10px",
                      right: "20px",
                      backgroundColor: "#10b981",
                      color: "white",
                      padding: "0.25rem 0.75rem",
                      borderRadius: "999px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {plan.badge}
                  </div>
                )}

                <div style={{ marginBottom: "1.5rem" }}>
                  <h3
                    style={{
                      color: "white",
                      fontSize: "1.25rem",
                      fontWeight: 600,
                      margin: "0 0 0.5rem 0",
                    }}
                  >
                    {plan.name}
                  </h3>
                  <p
                    style={{
                      color: "#9ca3af",
                      fontSize: "0.875rem",
                      margin: "0 0 1rem 0",
                    }}
                  >
                    {plan.subtitle}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "0.5rem",
                    }}
                  >
                    <span
                      style={{
                        color: "white",
                        fontSize: "2rem",
                        fontWeight: 700,
                      }}
                    >
                      {plan.priceDisplay}
                    </span>
                    {plan.period && (
                      <span style={{ color: "#9ca3af", fontSize: "0.9rem" }}>
                        {plan.period}
                      </span>
                    )}
                  </div>
                  {plan.billedText && (
                    <p
                      style={{
                        color: "#6b7280",
                        fontSize: "0.75rem",
                        margin: "0.25rem 0 0 0",
                      }}
                    >
                      {plan.billedText}
                    </p>
                  )}
                </div>

                <div style={{ flex: 1, marginBottom: "1.5rem" }}>
                  <p
                    style={{
                      color: "#9ca3af",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      margin: "0 0 1rem 0",
                    }}
                  >
                    Features
                  </p>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.75rem",
                    }}
                  >
                    {plan.features.map((feature, idx) => {
                      let iconSrc = "/json-media/icons/singlegreen.svg";
                      let iconSize = "20px";
                      let iconMarginRight = "0px";

                      if (feature.tier !== "double") {
                        iconMarginRight = "10px";
                      }

                      if (feature.tier === "yellow") {
                        iconSrc = "/json-media/icons/yellowtick.svg";
                      } else if (feature.tier === "double") {
                        iconSrc = "/json-media/icons/doublegreen.svg";
                        iconSize = "30px";
                      }

                      return (
                        <li
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "0.75rem",
                            color: "#cbd5e1",
                            fontSize: "0.875rem",
                            lineHeight: "1.5",
                          }}
                        >
                          <img
                            src={iconSrc}
                            alt="feature icon"
                            style={{
                              flexShrink: 0,
                              width: iconSize,
                              height: iconSize,
                              marginTop: "2px",
                              marginRight: iconMarginRight,
                            }}
                          />
                          <span>{feature.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <button
                  onClick={() => handlePlanSelect(plan)}
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "0.875rem",
                    backgroundColor: isLoading ? "#1e40af" : "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    cursor: loading ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                    opacity: loading ? 0.7 : 1,
                  }}
                  onMouseOver={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = "#2563eb";
                      e.target.style.transform = "translateY(-2px)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = "#3b82f6";
                      e.target.style.transform = "translateY(0)";
                    }
                  }}
                >
                  {isLoading ? "Processing..." : "SELECT PLAN"}
                </button>
              </div>
            );
          })}
        </div>

        <div
          style={{
            padding: "1.5rem 2rem",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            backgroundColor: "rgba(59, 130, 246, 0.05)",
          }}
        >
          <p
            style={{
              color: "#94a3b8",
              fontSize: "0.875rem",
              margin: 0,
              textAlign: "center",
            }}
          >
            💡 Your current plan will be changed immediately after payment
            confirmation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingModal;
